/** @jsxImportSource ./simboluri-jsx */
// Placeholder-ul modelului 3D: scheletul clădirii, într-un singur ton, cât
// timp rezultatul Pasului 7 se descarcă.
//
// Ce se vede: amprenta punctată pe teren, apoi parterul — stâlpii și placa
// lui, străvezie, ca stâlpii să rămână vizibili — apoi, la câteva secunde,
// fiecare etaj următor. O bandă mai închisă urcă prin ce a apărut, tot timpul:
// e semnul că se lucrează, nu că s-a oprit. Nivelurile NU reîncep de la zero:
// dacă descărcarea durează, scheletul complet stă și lucește până vine
// modelul, care îl înlocuiește la aceeași mărime și din același unghi
// (geometria e în schelet.ts; camera e copia celei din Scene3D).
//
// Nimic nu e desen la scară în plus față de siluetă: înălțimile sunt cele
// reale, tocmai ca modelul să cadă peste schelet fără să sară.

import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { useT } from "../i18n";
import type { Silueta } from "../api";
import { camera, feteVizibile, nivelurileSiluetei, stalpi } from "./schelet";

// ritmul: un nivel nou la fiecare PAS secunde; banda urcă o dată la LUCIU
const PAS_S = 2.4;
const LUCIU_S = 1.7;
// grosimea plăcii, ca fracțiune din înălțimea nivelului; lățimea stâlpului în px
const PLACA = 0.07;
const STALP_PX = 3.2;

export default function ScheletIncarcare({ silueta }: { silueta?: Silueta | null }) {
  const gazda = useRef<HTMLDivElement>(null);
  const [marime, setMarime] = useState<{ w: number; h: number } | null>(null);
  const id = useId().replace(/:/g, "");

  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    const masoara = () =>
      setMarime({ w: el.clientWidth || 900, h: el.clientHeight || 560 });
    const ro = new ResizeObserver(masoara);
    ro.observe(el);
    masoara();
    return () => ro.disconnect();
  }, []);

  const t = useT();
  const niveluri = nivelurileSiluetei(silueta);
  const cam = marime ? camera(niveluri, marime.w, marime.h) : null;

  return (
    <div ref={gazda} className="ob-schelet" aria-busy="true"
         aria-label={t("struct.schelet.incarca")}>
      {cam && marime && (
        <svg viewBox={`0 0 ${marime.w} ${marime.h}`} width="100%" height="100%"
             style={{ "--ob-schelet-jos": `${marime.h + 80}px` } as CSSProperties}
             aria-hidden="true">
          <defs>
            <linearGradient id={`${id}-lum`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--hair)" stopOpacity="0" />
              <stop offset="0.5" stopColor="var(--hair)" stopOpacity="0.55" />
              <stop offset="1" stopColor="var(--hair)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* amprenta pe teren, de la început */}
          <polygon className="ob-schelet-lot"
                   points={niveluri[0].poly.map(([x, y]) => cam.P(x, y, 0).join(",")).join(" ")} />
          {niveluri.map((n, i) => {
            const z0 = n.z;
            const z1 = n.z + n.h;
            const zPlaca = z1 - PLACA * n.h;
            const forme: JSX.Element[] = [];
            // stâlpii, din spate în față
            const puncte = stalpi(n.poly)
              .sort((a, b) => cam.adancime(b[0], b[1], z0) - cam.adancime(a[0], a[1], z0));
            puncte.forEach(([x, y], k) => {
              const [bx, by] = cam.P(x, y, z0);
              const [tx, ty] = cam.P(x, y, zPlaca);
              const w2 = STALP_PX / 2;
              forme.push(
                <polygon key={`s${k}`}
                         points={`${bx - w2},${by} ${bx + w2},${by} ${tx + w2},${ty} ${tx - w2},${ty}`} />);
            });
            // grosimea plăcii: fețele întoarse spre cameră
            feteVizibile(n.poly, zPlaca, cam).forEach((f, k) => {
              forme.push(
                <polygon key={`f${k}`}
                         points={[cam.P(f.a[0], f.a[1], zPlaca), cam.P(f.c[0], f.c[1], zPlaca),
                                  cam.P(f.c[0], f.c[1], z1), cam.P(f.a[0], f.a[1], z1)]
                           .map((p) => p.join(",")).join(" ")} />);
            });
            const sus = n.poly.map(([x, y]) => cam.P(x, y, z1).join(",")).join(" ");
            return (
              <g key={i} className="ob-schelet-niv" style={{ animationDelay: `${i * PAS_S}s` }}>
                <clipPath id={`${id}-n${i}`}>
                  {forme}
                  <polygon points={sus} />
                </clipPath>
                <g className="ob-schelet-beton">{forme}</g>
                <polygon className="ob-schelet-placa" points={sus} />
                <g clipPath={`url(#${id}-n${i})`}>
                  <rect className="ob-schelet-lum" x="0" y="0" width={marime.w} height="56"
                        fill={`url(#${id}-lum)`}
                        style={{ animationDuration: `${LUCIU_S}s` }} />
                </g>
              </g>
            );
          })}
        </svg>
      )}
      <div className="ob-schelet-text lbl">{t("struct.schelet.incarca")}</div>
    </div>
  );
}
